/**
 * User Access Panel — Phase 1.5 of FEATURE_ROADMAP §19.
 *
 * Lives below the SSO card on /integrations. Lets tenant admins see who has
 * migrated to SSO, who hasn't, and selectively force individual users to
 * SSO-only. Also exposes break-glass admin management — the "always-on
 * password access" recovery path so a broken IdP can't lock the tenant out.
 *
 * Invariants the UI helps enforce:
 *   - At least one break-glass admin per tenant (server refuses to clear
 *     the last one; UI shows a warning at <2)
 *   - Break-glass users can't be password-disabled (toggle locked + tooltip)
 *   - Break-glass status only available to admin-role users
 */
import { useEffect, useState } from 'react'
import {
  Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, Alert,
  CircularProgress, Switch, FormControlLabel, Snackbar,
} from '@mui/material'
import {
  VpnKey, Key, CheckCircle, Cancel, Warning as WarningIcon, Refresh,
} from '@mui/icons-material'
import { tenantApi } from '../services/api'

interface UserRow {
  id: string
  email: string
  name: string
  role: string
  status: string
  has_password: boolean
  sso_subject: string | null
  last_login: string | null
  last_sso_login_at: string | null
  password_auth_disabled: boolean
  is_break_glass: boolean
}

interface Summary {
  total: number
  password_only_never_sso: number
  sso_linked_with_password: number
  sso_only: number
  no_auth_set_up: number
  break_glass_admins: number
}

interface Props {
  tenantId: string
  ssoEnabled: boolean
}

const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString() : '—'

const UserAccessPanel = ({ tenantId, ssoEnabled }: Props) => {
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState<UserRow[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [error, setError] = useState<string>('')
  const [pendingUserId, setPendingUserId] = useState<string>('')
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' })

  const load = async () => {
    setLoading(true); setError('')
    try {
      const res = await tenantApi.listUsersAccess(tenantId)
      setUsers(res.data.users || [])
      setSummary(res.data.summary || null)
    } catch (e: any) {
      const d = e.response?.data?.detail
      setError(typeof d === 'string' ? d : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (tenantId) load() }, [tenantId])

  const onAction = async (userId: string, fn: () => Promise<any>, okMsg: string) => {
    setPendingUserId(userId); setError('')
    try {
      await fn()
      setSnack({ open: true, msg: okMsg, sev: 'success' })
      await load()
    } catch (e: any) {
      const d = e.response?.data?.detail
      const msg = typeof d === 'string' ? d : 'Action failed'
      setSnack({ open: true, msg, sev: 'error' })
    } finally {
      setPendingUserId('')
    }
  }

  const togglePasswordAuth = (u: UserRow) => {
    if (u.is_break_glass) {
      setSnack({ open: true, msg: 'Cannot disable password for a break-glass admin. Clear break-glass first.', sev: 'error' })
      return
    }
    if (u.password_auth_disabled) {
      if (!confirm(`Re-enable password sign-in for ${u.email}? They'll be able to log in with either password or SSO.`)) return
      return onAction(u.id, () => tenantApi.enablePasswordAuth(u.id), `Password sign-in re-enabled for ${u.email}`)
    }
    if (!confirm(`Disable password sign-in for ${u.email}? They'll only be able to log in via SSO. Make sure they've successfully used SSO at least once first.`)) return
    return onAction(u.id, () => tenantApi.disablePasswordAuth(u.id), `Password sign-in disabled for ${u.email}`)
  }

  const toggleBreakGlass = (u: UserRow) => {
    if (u.is_break_glass) {
      if (!confirm(`Remove break-glass status from ${u.email}? They'll lose the SSO-recovery override. The action will be refused if they're the last break-glass admin in this tenant.`)) return
      return onAction(u.id, () => tenantApi.clearBreakGlass(u.id), `Break-glass cleared for ${u.email}`)
    }
    if (u.role !== 'admin') {
      setSnack({ open: true, msg: 'Only admin-role users can be break-glass. Promote them to admin first.', sev: 'error' })
      return
    }
    if (!u.has_password) {
      setSnack({ open: true, msg: 'Break-glass user must have a password set. Set a password first via Team management.', sev: 'error' })
      return
    }
    if (!confirm(`Mark ${u.email} as break-glass? They'll retain password sign-in even if you later force SSO-only for the tenant. Use sparingly — typically 1-2 admins per tenant.`)) return
    return onAction(u.id, () => tenantApi.setBreakGlass(u.id), `Break-glass granted to ${u.email}`)
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
  }

  const onlyOneBreakGlass = summary && summary.break_glass_admins === 1
  const zeroBreakGlass = summary && summary.break_glass_admins === 0

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6">User Access</Typography>
            <Typography variant="body2" color="text.secondary">
              See who has migrated to SSO. Force individual users to SSO-only as you transition off passwords. Maintain at least one break-glass admin for SSO-outage recovery.
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load}><Refresh /></IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {zeroBreakGlass && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>No break-glass admin configured.</strong> Promote at least one admin to break-glass status — without one, a broken IdP or revoked SSO config could lock the whole tenant out.
          </Alert>
        )}
        {onlyOneBreakGlass && ssoEnabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Only <strong>1 break-glass admin</strong> for this tenant. Recommended: <strong>2</strong>, so the second can recover access if the first loses their password.
          </Alert>
        )}

        {/* Summary chips */}
        {summary && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip size="small" label={`Total: ${summary.total}`} variant="outlined" />
            <Chip size="small" color="default" variant="outlined"
              label={`${summary.password_only_never_sso} password-only (never SSO'd)`} />
            <Chip size="small" color="info" variant="outlined"
              label={`${summary.sso_linked_with_password} SSO-linked (both auth paths)`} />
            <Chip size="small" color="success" variant="outlined"
              label={`${summary.sso_only} SSO-only (password disabled)`} />
            <Chip size="small" color={summary.break_glass_admins > 0 ? 'warning' : 'error'} variant="outlined"
              icon={<Key sx={{ fontSize: 14 }} />}
              label={`${summary.break_glass_admins} break-glass admin${summary.break_glass_admins === 1 ? '' : 's'}`} />
          </Box>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Last password login</TableCell>
                <TableCell>Last SSO login</TableCell>
                <TableCell align="center">Auth state</TableCell>
                <TableCell align="center">Force SSO only</TableCell>
                <TableCell align="center">Break-glass</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography variant="body2">{u.name || '(no name)'}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={u.role} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmt(u.last_login)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmt(u.last_sso_login_at)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {u.password_auth_disabled ? (
                      <Tooltip title="Password sign-in disabled — SSO only">
                        <Chip icon={<VpnKey sx={{ fontSize: 14 }} />} label="SSO only" size="small" color="success" />
                      </Tooltip>
                    ) : u.sso_subject ? (
                      <Tooltip title="Has used both password and SSO">
                        <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Both" size="small" color="info" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Has password but has never signed in via SSO">
                        <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Password only" size="small" variant="outlined" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={u.is_break_glass
                      ? "Break-glass admins cannot be password-disabled — that's the whole point"
                      : (u.password_auth_disabled ? 'Click to re-enable password sign-in' : 'Click to force SSO-only for this user')}>
                      <span>
                        <Switch
                          size="small"
                          checked={u.password_auth_disabled}
                          disabled={u.is_break_glass || pendingUserId === u.id}
                          onChange={() => togglePasswordAuth(u)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={
                      u.is_break_glass ? 'Click to clear break-glass status' :
                      u.role !== 'admin' ? 'Only admin-role users can be break-glass' :
                      !u.has_password ? 'User must have a password set before becoming break-glass' :
                      'Click to mark as break-glass (always retains password access)'
                    }>
                      <span>
                        <Switch
                          size="small"
                          checked={u.is_break_glass}
                          disabled={(!u.is_break_glass && (u.role !== 'admin' || !u.has_password)) || pendingUserId === u.id}
                          onChange={() => toggleBreakGlass(u)}
                          color="warning"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No users in this tenant</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!ssoEnabled && summary && summary.total > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            SSO isn't enabled yet for this tenant. Disabling password sign-in is allowed but users with neither auth path set up won't be able to log in. Configure SSO above first.
          </Alert>
        )}
      </CardContent>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })} sx={{ minWidth: 320 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Card>
  )
}

export default UserAccessPanel
